<template>
  <div class="fluid">
    <div ref="panZoomParentElement" class="image-preview" :class="{ active: isGrabbing }">
      <div ref="panZoomElement" class="panzoom-element">
        <img ref="panZoomImage" :alt="alt" :style="{ transform: `rotate(${rotateDegree}deg)` }" />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, onUnmounted, ref, watch } from 'vue'
import panZoom from 'panzoom'

import type { PanZoom } from 'panzoom'

const props = withDefaults(
  defineProps<{
    src: string
    alt?: string
  }>(),
  {
    alt: '',
  },
)

export type ImagePreviewControls = {
  zoomIn: () => void
  zoomOut: () => void
  rotate: () => void
  reset: () => void
}

const panZoomParentElement = ref<HTMLElement>()
const panZoomElement = ref<HTMLElement>()
const panZoomImage = ref<HTMLImageElement>()
const panZoomInstance = ref<PanZoom>()
const rotateCount = ref(0)
const rotateDegree = ref(0)
const isGrabbing = ref(false)
const maxInitialZoom = 1.5
const imageWidth = ref(0)
const imageHeight = ref(0)

const disposePanZoom = () => {
  if (panZoomInstance.value) {
    panZoomInstance.value.dispose()
    panZoomInstance.value = undefined
  }
}

const getInitialZoomAndCoordinates = () => {
  if (panZoomParentElement.value && imageWidth.value && imageHeight.value) {
    const el = panZoomParentElement.value.getBoundingClientRect()
    const zoom = Math.min(el.width / imageWidth.value, el.height / imageHeight.value, maxInitialZoom)
    const x = (el.width - el.width * zoom) / 2
    const y = (el.height - el.height * zoom) / 2

    return { x, y, zoom }
  }

  return { x: 0, y: 0, zoom: 1 }
}

const loadImage = () => {
  disposePanZoom()
  rotateCount.value = 0
  rotateDegree.value = 0

  const requestedSrc = props.src
  const newImg = new Image()
  newImg.src = requestedSrc

  newImg.onload = () => {
    if (requestedSrc !== props.src) return

    if (panZoomElement.value && panZoomImage.value) {
      panZoomImage.value.src = requestedSrc
      imageWidth.value = newImg.width
      imageHeight.value = newImg.height

      const { x, y, zoom } = getInitialZoomAndCoordinates()

      panZoomInstance.value = panZoom(panZoomElement.value, {
        initialZoom: zoom,
      })

      panZoomInstance.value.moveTo(x, y)

      panZoomInstance.value.on('panstart', () => {
        isGrabbing.value = true
      })

      panZoomInstance.value.on('panend', () => {
        isGrabbing.value = false
      })
    }
  }
}

const reset = () => {
  if (panZoomInstance.value) {
    const { x, y, zoom } = getInitialZoomAndCoordinates()

    panZoomInstance.value.pause()
    panZoomInstance.value.resume()
    panZoomInstance.value.zoomAbs(0, 0, zoom)
    panZoomInstance.value.moveTo(x, y)
    rotateCount.value = 0
    rotateDegree.value = 0
  }
}

const zoomIn = () => {
  if (panZoomInstance.value && panZoomParentElement.value) {
    const { width, height } = panZoomParentElement.value.getBoundingClientRect()
    panZoomInstance.value.smoothZoom(width / 2, height / 2, 1.25)
  }
}

const zoomOut = () => {
  if (panZoomInstance.value && panZoomParentElement.value) {
    const { width, height } = panZoomParentElement.value.getBoundingClientRect()
    panZoomInstance.value.smoothZoom(width / 2, height / 2, 0.75)
  }
}

const rotate = () => {
  rotateCount.value += 1
  rotateDegree.value = (rotateCount.value % 4) * 90
}

watch(() => props.src, loadImage)
onMounted(loadImage)
onUnmounted(disposePanZoom)

defineExpose<ImagePreviewControls>({
  zoomIn,
  zoomOut,
  rotate,
  reset,
})
</script>

<style lang="scss" scoped>
.fluid {
  position: relative;
  height: 100%;
  width: 100%;
}

.image-preview {
  display: block;
  width: 100%;
  height: 100%;
  background-color: #525659;
  overflow: hidden;
  cursor: grab;
  position: relative;

  &.active {
    cursor: grabbing;
  }
}

.panzoom-element {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  inset: 0;
}
</style>
